/**
 * @fileoverview All Milestones Query Hook
 * 
 * Fetches milestones across all jobs within a date range, with optional filtering
 * by specific job. Used primarily for calendar views and milestone dashboards where
 * cross-job visibility is needed.
 * 
 * Filtering:
 * - Date range: Required start and end dates to bound the query
 * - Job ID: Optional filter to show only milestones from a specific job
 * - Blank milestones: Automatically excluded (is_blank = false)
 * 
 * The hook respects user authentication and only queries when a valid user session
 * exists. Results are ordered by due date ascending for chronological display.
 * 
 * Common Use Cases:
 * - Calendar view showing all upcoming milestones
 * - Project manager dashboard with cross-job deadlines
 * - Gantt chart visualization of multiple projects
 * - Milestone report filtered by date range
 * 
 * Key Features:
 * - React Query for caching and automatic refetching
 * - Date range filtering for performance
 * - Optional job-specific filtering
 * - Excludes placeholder/blank milestones
 * - Chronological ordering by due date
 * 
 * @module useAllMilestones
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { Milestone } from '@/lib/types/milestone';

/**
 * Parameters for fetching milestones
 */
interface UseAllMilestonesParams {
  /** Start of date range (inclusive) */
  startDate: Date;
  /** End of date range (inclusive) */
  endDate: Date;
  /** Optional job ID to filter milestones */
  jobIdFilter?: string;
}

/**
 * Hook for fetching milestones across all jobs within a date range
 * 
 * Queries the milestones table for all non-blank milestones that fall within
 * the specified date range. Optionally filters to a single job if jobIdFilter
 * is provided.
 * 
 * Query Behavior:
 * - Requires authenticated user
 * - Filters by date range (gte startDate, lte endDate)
 * - Excludes blank/placeholder milestones
 * - Orders results by due_date ascending
 * - Caches results with React Query
 * - Auto-refetches on parameter changes
 * 
 * Performance Considerations:
 * - Keep date ranges reasonable (e.g., 3-6 months) for optimal performance
 * - Query is disabled when user is not authenticated
 * - Results are cached and reused when parameters match
 * 
 * @param params - Query parameters
 * @returns React Query result object with milestone data
 * 
 * @example
 * ```tsx
 * function MilestoneCalendar() {
 *   const [dateRange, setDateRange] = useState({
 *     start: startOfMonth(new Date()),
 *     end: endOfMonth(new Date())
 *   });
 * 
 *   const { data: milestones, isLoading } = useAllMilestones({
 *     startDate: dateRange.start,
 *     endDate: dateRange.end
 *   });
 * 
 *   if (isLoading) return <Spinner />;
 * 
 *   return (
 *     <Calendar>
 *       {milestones?.map(milestone => (
 *         <CalendarEvent
 *           key={milestone.id}
 *           date={milestone.due_date}
 *           title={milestone.title}
 *         />
 *       ))}
 *     </Calendar>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Filter to specific job
 * function JobMilestoneTimeline({ jobId }) {
 *   const { data: milestones } = useAllMilestones({
 *     startDate: new Date(),
 *     endDate: addMonths(new Date(), 6),
 *     jobIdFilter: jobId
 *   });
 * 
 *   return (
 *     <Timeline>
 *       {milestones?.map(m => (
 *         <TimelineEvent key={m.id} date={m.due_date} />
 *       ))}
 *     </Timeline>
 *   );
 * }
 * ```
 */
export const useAllMilestones = ({ startDate, endDate, jobIdFilter }: UseAllMilestonesParams) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['all-milestones', startDate.toISOString(), endDate.toISOString(), jobIdFilter, user?.role],
    queryFn: async (): Promise<Milestone[]> => {
      if (!user) throw new Error('User not authenticated');

      // Build base query with date range filter
      let query = supabase
        .from('milestones')
        .select('*')
        .gte('due_date', startDate.toISOString())
        .lte('due_date', endDate.toISOString())
        .eq('is_blank', false)  // Exclude placeholder milestones
        .order('due_date', { ascending: true });

      // Apply job filter if provided
      if (jobIdFilter) {
        query = query.eq('job_id', jobIdFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []) as Milestone[];
    },
    enabled: !!user,
  });
};